import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ArrowRight,
  RotateCcw,
  Download,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { reportError } from '@/lib/sentry';
import { ImportFieldDef, detectMapping, ParseResult, RowIssue } from '@/lib/import-utils';
import { readSpreadsheetFile } from '@/lib/excel-parse';
import type { TemplateKind } from '@/lib/import-templates';
import { cn } from '@/lib/utils';

export interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

export interface ImportKindConfig {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  fields: ImportFieldDef[];
  supportsMode?: boolean;
  templateKind: TemplateKind;
  importNote?: string;
  parse: (
    rows: Record<string, unknown>[],
    mapping: Record<string, string>,
    rawTypedRows?: Record<string, unknown>[]
  ) => ParseResult;
  buildPayload: (parsed: ParseResult) => Record<string, any>[];
}

interface ExcelImportWizardProps {
  kinds: ImportKindConfig[];
  defaultKind?: string;
  title?: string;
  description?: string;
  notice?: ReactNode;
  onImport: (kind: string, rows: Record<string, any>[], mode: 'create' | 'upsert') => Promise<ImportResult | null>;
  onComplete?: (result: ImportResult) => void;
}

export function ExcelImportWizard({
  kinds,
  defaultKind,
  title = 'Importar desde Excel',
  description = 'Sube tu archivo, revisa el mapeo y confirma la importación.',
  notice,
  onImport,
  onComplete,
}: ExcelImportWizardProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [kindKey, setKindKey] = useState(defaultKind || kinds[0]?.key || '');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, unknown>[]>([]);
  const [rawTypedRows, setRawTypedRows] = useState<Record<string, unknown>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [mode, setMode] = useState<'create' | 'upsert'>('create');
  const [result, setResult] = useState<ImportResult | null>(null);

  const config = kinds.find((item) => item.key === kindKey) || kinds[0];
  const fields = config?.fields || [];

  const parsed = useMemo(
    () => (config ? config.parse(rawRows, mapping, rawTypedRows) : { rows: [], issues: [], validCount: 0 }),
    [config, rawRows, mapping, rawTypedRows]
  );

  const issueByRow = useMemo(() => {
    const map = new Map<number, RowIssue>();
    parsed.issues.forEach((issue) => map.set(issue.row, issue));
    return map;
  }, [parsed.issues]);

  const reset = () => {
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setRawTypedRows([]);
    setMapping({});
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKindChange = (next: string) => {
    setKindKey(next);
    reset();
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !config) return;

    setIsParsing(true);
    setResult(null);
    try {
      const data = await readSpreadsheetFile(file);
      if (!data.rows.length) throw new Error('El archivo no contiene filas de datos.');

      setFileName(file.name);
      setHeaders(data.headers);
      setRawRows(data.rows);
      setRawTypedRows(data.rawRows);
      setMapping(detectMapping(data.headers, config.fields));

      toast({
        title: 'Archivo leído',
        description: `${data.rows.length} fila(s) detectadas. Revisa el mapeo de columnas.`,
      });
    } catch (error: any) {
      reportError(error, { operation: 'import_parse_file', area: 'clients' });
      toast({
        title: 'No se pudo leer el archivo',
        description: error?.message || 'Formato no reconocido. Usa un archivo .xlsx o .csv.',
        variant: 'destructive',
      });
    } finally {
      setIsParsing(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!config) return;
    setIsDownloading(true);
    try {
      const { downloadImportTemplate } = await import('@/lib/import-templates');
      await downloadImportTemplate(config.templateKind);
      toast({
        title: 'Plantilla descargada',
        description: 'Llena la hoja Datos y súbela aquí.',
      });
    } catch (error: any) {
      reportError(error, { operation: 'import_download_template', area: 'clients' });
      toast({
        title: 'No se pudo generar la plantilla',
        description: error?.message || 'Intenta de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImport = async () => {
    if (!config) return;
    const payload = config.buildPayload(parsed);
    if (!payload.length) {
      toast({
        title: 'No hay filas válidas',
        description: 'Corrige las filas con errores antes de importar.',
        variant: 'destructive',
      });
      return;
    }

    setIsImporting(true);
    setResult(null);
    try {
      const summary = await onImport(config.key, payload, mode);
      if (!summary) return;

      setResult(summary);
      toast({
        title: 'Importación completada',
        description: `${summary.inserted} creadas · ${summary.updated} actualizadas · ${summary.skipped} omitidas`,
      });
      onComplete?.(summary);
    } catch (error: any) {
      reportError(error, { operation: 'import_run_batch', area: 'clients' });
      toast({
        title: 'Error al importar',
        description: error?.message || 'No se pudo completar la importación.',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const previewRows = parsed.rows.slice(0, 10);

  if (!config) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {fileName && (
          <Button variant="outline" size="sm" onClick={reset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Empezar de nuevo
          </Button>
        )}
      </div>

      {notice}

      {kinds.length > 1 && (
        <Card className="glass-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">1. ¿Qué quieres importar?</CardTitle>
            <CardDescription>
              Las membresías se vinculan a las clientas por teléfono, así que importa primero las clientas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {kinds.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleKindChange(item.key)}
                    className={cn(
                      'flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-all',
                      kindKey === item.key
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    )}
                  >
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <span>
                      <span className="block font-semibold">{item.label}</span>
                      <span className="text-xs text-muted-foreground">{item.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{kinds.length > 1 ? '2.' : '1.'} Sube el archivo</CardTitle>
          <CardDescription>Formatos admitidos: .xlsx, .xls o .csv. Se procesa en tu navegador.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFile}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isParsing}>
              {isParsing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Seleccionar archivo
            </Button>
            <Button variant="outline" onClick={handleDownloadTemplate} disabled={isDownloading}>
              {isDownloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Descargar plantilla
            </Button>
            {fileName && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileSpreadsheet className="h-4 w-4" />
                {fileName} · {rawRows.length} fila(s)
              </span>
            )}
          </div>

          <details className="rounded-lg border border-border/60 p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Columnas esperadas ({fields.length})
            </summary>
            <ul className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2 lg:grid-cols-3">
              {fields.map((field) => (
                <li key={field.key}>
                  {field.label}
                  {field.required && <span className="text-destructive"> *</span>}
                </li>
              ))}
            </ul>
          </details>
        </CardContent>
      </Card>

      {fileName && (
        <>
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{kinds.length > 1 ? '3.' : '2.'} Revisa el mapeo de columnas</CardTitle>
              <CardDescription>
                Ajusta a qué columna del archivo corresponde cada dato. Las columnas obligatorias están marcadas.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {fields.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label className="text-xs">
                      {field.label}
                      {field.required && <span className="text-destructive"> *</span>}
                    </Label>
                    <Select
                      value={mapping[field.key] ?? '__none__'}
                      onValueChange={(value) =>
                        setMapping((prev) => {
                          const next = { ...prev };
                          if (value === '__none__') delete next[field.key];
                          else next[field.key] = value;
                          return next;
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sin asignar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sin asignar</SelectItem>
                        {headers.map((header) => (
                          <SelectItem key={header} value={header}>
                            {header}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.hint && <p className="text-[11px] text-muted-foreground">{field.hint}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{kinds.length > 1 ? '4.' : '3.'} Previsualización</CardTitle>
              <CardDescription>
                {parsed.validCount} de {parsed.rows.length} fila(s) listas para importar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {parsed.issues.length > 0 && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    {parsed.issues.length} fila(s) con problemas (se omiten al importar)
                  </p>
                  <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto text-xs text-muted-foreground">
                    {parsed.issues.slice(0, 20).map((issue) => (
                      <li key={issue.row}>
                        Fila {issue.row}: {issue.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Fila</TableHead>
                      {fields.map((field) => (
                        <TableHead key={field.key}>{field.label}</TableHead>
                      ))}
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewRows.map((row) => {
                      const issue = issueByRow.get(row.index);
                      return (
                        <TableRow key={row.index} className={issue ? 'bg-destructive/5' : undefined}>
                          <TableCell className="text-muted-foreground">{row.index}</TableCell>
                          {fields.map((field) => (
                            <TableCell key={field.key} className="max-w-[180px] truncate">
                              {row.values[field.key] === null || row.values[field.key] === undefined
                                ? '-'
                                : String(row.values[field.key])}
                            </TableCell>
                          ))}
                          <TableCell>
                            {issue ? (
                              <Badge variant="destructive">Revisar</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">
                                Lista
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {parsed.rows.length > previewRows.length && (
                <p className="text-xs text-muted-foreground">
                  Mostrando las primeras {previewRows.length} de {parsed.rows.length} filas.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{kinds.length > 1 ? '5.' : '4.'} Importar</CardTitle>
              <CardDescription>
                {config.importNote || 'Revisa la previsualización antes de confirmar.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.supportsMode && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={mode === 'create' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMode('create')}
                  >
                    Solo crear nuevas
                  </Button>
                  <Button
                    variant={mode === 'upsert' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMode('upsert')}
                  >
                    Crear y actualizar existentes
                  </Button>
                </div>
              )}

              <Button onClick={handleImport} disabled={isImporting || parsed.validCount === 0}>
                {isImporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                Importar {parsed.validCount} fila(s)
              </Button>

              {result && (
                <div className="space-y-2 rounded-lg border border-border p-4">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Importación finalizada
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="success">{result.inserted} creadas</Badge>
                    <Badge variant="outline">{result.updated} actualizadas</Badge>
                    <Badge variant="secondary">{result.skipped} omitidas</Badge>
                  </div>
                  {result.errors.length > 0 && (
                    <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-muted-foreground">
                      {result.errors.map((err, index) => (
                        <li key={`${err.row}-${index}`}>
                          Fila {err.row}: {err.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
